import { useEffect, useState } from 'react'

import { Box, Button, Text, useConfirmationDialog } from '@factorialco/deprecated-design-system'
import {
  ContactFragment,
  FinancialProjectForSelectFragment,
  LegalEntityForSelectFragment,
  PreviewSaleFinancialDocumentDocument,
  RegisterSaleFinancialDocumentDocument,
  SaleInvoiceUpsertSidepanelDocument,
  SaleInvoicesListTableDocument,
  SimpleError,
  StructuredError,
  TaxRateForSelectFragment,
  UpsertSaleFinancialDocumentDocument,
  ValidateSaleForIssueFinancialDocumentDocument,
} from 'generated/resources/graphql'
import { debounce } from 'lodash'

import NotFound from 'modules/core/components/NotFound'
import Sidepanel from 'modules/core/components/Sidepanel'
import { useUnsafeCallback } from 'modules/core/hooks/useCallback'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useUnsafeEffect } from 'modules/core/hooks/useEffect'
import { useForm } from 'modules/core/hooks/useForm'
import { useUnsafeMemo } from 'modules/core/hooks/useMemo'
import { useMutation } from 'modules/core/hooks/useMutation'
import { useQuery } from 'modules/core/hooks/useQuery'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { useArchiveSaleInvoiceDialog } from 'modules/finance/hooks/sale_invoices/useArchiveSaleInvoiceDialog'
import { useDuplicateSaleInvoice } from 'modules/finance/hooks/sale_invoices/useDuplicateSaleInvoice'
import handleResultWithFlash from 'modules/finance/lib/handleResultWithFlash'
import Loading from 'modules/layout/components/Page/Loading'

import { SaleInvoiceForm } from './SaleInvoiceForm'
import { SaleInvoiceFormFields } from './SaleInvoiceForm/types'
import { createDefaultFormValues, getLegalEntityToInitialize } from './lib/defaultValuesUtils'
import { fetchLogoFile, handleErrorValidateForIssuing, trackFormSaveAction } from './lib/formUtils'
import {
  mapComplianceExtraFieldsForUpsert,
  mapLineItemsForPreview,
  mapLineItemsForUpsert,
} from './lib/mappingUtils'
import {
  isValidForIssuing,
  validateComplianceExtraFields,
  validateLineItems,
  validateSaleInvoiceForm,
} from './lib/validationUtils'
import { SaleInvoiceStatus } from './saleInvoiceStatus'

interface SaleInvoicesUpsertSidePanelProps {
  invoiceId?: string
  onClose: () => void
  defaultLegalEntity?: LegalEntityForSelectFragment | null
  defaultCurrency?: string
  financialProject?: FinancialProjectForSelectFragment | null
  contact?: ContactFragment | null
  createCreditNoteProp?: boolean
  originPageInvoices?: boolean
}

const SaleInvoicesUpsertSidePanel = ({
  invoiceId,
  onClose,
  defaultLegalEntity,
  defaultCurrency,
  financialProject,
  contact,
  createCreditNoteProp,
  originPageInvoices = true,
}: SaleInvoicesUpsertSidePanelProps) => {
  const [salesInvoiceId, setSalesInvoiceId] = useState<number>(invoiceId ? Number(invoiceId) : -1)
  const { company, employee: currentEmployee } = useCurrentSession()
  const { openDialog } = useConfirmationDialog()
  const { handleDuplicateSaleInvoice } = useDuplicateSaleInvoice()

  const { showArchiveSaleInvoiceDialog } = useArchiveSaleInvoiceDialog()
  const [previewHtml, setPreviewHtml] = useState<string | undefined>()
  const [createCreditNote, setCreateCreditNote] = useState<boolean>(createCreditNoteProp ?? false)

  const isSalesInvoicePersisted = !!salesInvoiceId && salesInvoiceId !== -1

  const { data, error, loading } = useQuery(SaleInvoiceUpsertSidepanelDocument, {
    variables: {
      companyId: company.id,
      id: salesInvoiceId,
      invoicePresent: isSalesInvoicePersisted,
      country: defaultLegalEntity?.country,
    },
  })

  const eventOrigin = originPageInvoices ? 'Invoices' : 'Projects'
  const saleInvoice = data?.finance?.financialDocument
  const taxRatesCounter: number = data?.finance.taxRatesConnection.totalCount ?? 0
  const defaultTaxRate: TaxRateForSelectFragment | undefined =
    taxRatesCounter == 1 ? data?.finance.taxRatesConnection.nodes[0] : undefined
  const contactsCounter = data?.finance.contactsConnection.totalCount ?? 0
  const personalIncomeTaxRatesCounter = data?.finance.personalIncomeTaxRates.totalCount ?? 0
  const projectsCounter = data?.projectManagement.financialProjectsConnection.totalCount ?? 0
  const projectsModuleEnabled =
    data?.companies.featureAvailabilitiesConnection.nodes[0]?.available ?? false

  const features = data?.features?.featuresConnection?.nodes
    .filter((feature) => feature.enabled)
    .map((feature) => feature.name)
  const taxAgencyIntegrationsEnabled = features?.includes('dev_tax_agency_integrations') ?? false

  const complianceExtraFieldsConfigs = useUnsafeMemo(
    () => data?.finance.financialDocumentComplianceExtraFieldsConfigs ?? [],
    [data?.finance.financialDocumentComplianceExtraFieldsConfigs]
  )
  const taxBridgeActivations = useUnsafeMemo(
    () => data?.finance.taxBridgeActivations ?? [],
    [data?.finance.taxBridgeActivations]
  )
  const spanishLegalEntities = useUnsafeMemo(
    () => data?.companies.spanishLegalEntities?.nodes ?? [],
    [data?.companies.spanishLegalEntities?.nodes]
  )
  const nonSpanishLegalEntity = useUnsafeMemo(
    () => data?.companies.nonSpanishLegalEntities?.nodes[0] ?? null,
    [data?.companies.nonSpanishLegalEntities?.nodes]
  )

  const defaultSalesDocumentTemplates = useUnsafeMemo(
    () => data?.finance.defaultSalesDocumentTemplates ?? [],
    [data?.finance.defaultSalesDocumentTemplates]
  )

  const canManageSalesDocumentTemplates = useUnsafeMemo(
    () => data?.finance._policies.salesDocumentTemplatesManage.can ?? false,
    [data?.finance._policies.salesDocumentTemplatesManage.can]
  )

  const defaultComplianceExtraFieldsSettings = useUnsafeMemo(
    () => data?.finance.defaultComplianceExtraFieldsSettings ?? [],
    [data?.finance.defaultComplianceExtraFieldsSettings]
  )

  useEffect(() => {
    form.setValues((oldValues) => ({
      ...oldValues,
      salesInvoiceSequence: data?.finance.financialDocument?.salesInvoiceSequence ?? undefined,
    }))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data])

  const [upsertSaleFinancialDocument, {}] = useMutation(UpsertSaleFinancialDocumentDocument, {
    extractorFn: (dataParam) => dataParam?.financeMutations.upsertSaleFinanceFinancialDocument,
    refetchQueries: [SaleInvoicesListTableDocument],
    awaitRefetchQueries: true,
    update(cache) {
      cache.evict({
        id: salesInvoiceId?.toString(),
        fieldName: 'FinanceFinancialDocument',
      })
    },
  })

  const [registerSaleFinancialDocument, {}] = useMutation(RegisterSaleFinancialDocumentDocument, {
    extractorFn: (dataParam) => dataParam?.financeMutations.registerSaleFinanceFinancialDocument,
    refetchQueries: [SaleInvoicesListTableDocument],
    awaitRefetchQueries: true,
    update(cache) {
      cache.evict({
        id: salesInvoiceId?.toString(),
        fieldName: 'FinanceFinancialDocument',
      })
    },
  })

  const [validateSaleForIssueFinancialDocument, {}] = useMutation(
    ValidateSaleForIssueFinancialDocumentDocument,
    {
      extractorFn: (dataParam) =>
        dataParam?.financeMutations.validateSaleForIssueFinanceFinancialDocument,
      refetchQueries: [SaleInvoicesListTableDocument],
      awaitRefetchQueries: true,
      update(cache) {
        cache.evict({
          id: salesInvoiceId?.toString(),
          fieldName: 'FinanceFinancialDocument',
        })
      },
    }
  )

  const [previewFinancialDocument, {}] = useMutation(PreviewSaleFinancialDocumentDocument, {
    extractorFn: (dataParam) => dataParam?.financeMutations.previewFinanceFinancialDocumentPreview,
  })

  const isIssuingBlocked = (values: SaleInvoiceFormFields) => {
    return (
      values.action === 'issuing' &&
      (!values.currency ||
        !values.salesInvoiceSequence ||
        !values.operationDate ||
        !values.dueDate ||
        !values.legalEntity ||
        !values.contact ||
        values.lineItems.length == 0 ||
        validateLineItems(values.lineItems).length > 0 ||
        validateComplianceExtraFields(
          values.complianceExtraFields,
          form.values.documentType == 'credit_note'
        ).length > 0)
    )
  }

  const handleSuccessValidateForIssuing = (upsertedId: number) => {
    track(trackingEvents.accounting.saleInvoices.clickIssueInvoice, {
      origin: eventOrigin,
    })
    openDialog({
      title: i18n.t('finance.sale_invoices.issue.title_v2'),
      content: i18n.t('finance.sale_invoices.issue.content'),
      illustration: 'alert',
      confirmLabel: i18n.t('finance.sale_invoices.issue.confirm_and_issue'),
      onConfirm: async () => {
        await registerSaleFinancialDocument({
          variables: {
            id: upsertedId,
          },
        })
        app.setFlash({
          type: 'correct',
          message: i18n.t('finance.invoices.detail.success_save'),
        })
        onClose()
      },
    })
  }

  const validateIssuingParams = (values: SaleInvoiceFormFields) => {
    const errors: Record<string, string> = {}

    if (!values.currency) {
      errors.currency = i18n.t('finance.invoices.detail.form.errors.currency_required')
    }
    if (!values.operationDate) {
      errors.operationDate = i18n.t('finance.invoices.detail.form.errors.operation_date_required')
    }
    if (!values.dueDate) {
      errors.dueDate = i18n.t('finance.invoices.detail.form.errors.due_date_required')
    }
    if (!values.legalEntity?.id) {
      errors.legalEntity = i18n.t('finance.invoices.detail.form.errors.legal_entity_required')
    }
    if (!values.contact?.id) {
      errors.contact = i18n.t('finance.invoices.detail.form.errors.contact_required')
    }
    if (!values.documentType) {
      errors.documentType = i18n.t('finance.invoices.detail.form.errors.document_type_required')
    }
    if (!values.salesInvoiceSequence?.id) {
      errors.salesInvoiceSequence = i18n.t('finance.invoices.detail.form.errors.sequence_required')
    }

    if (Object.keys(errors).length > 0) {
      form.setErrors(errors)
      return false
    }
    return true
  }

  const onSubmit = async (values: SaleInvoiceFormFields) => {
    if (isIssuingBlocked(values)) {
      return { success: false, data: {}, errors: [] }
    }

    const baseSubmitData = {
      lineItems: mapLineItemsForUpsert(values.lineItems),
      discountPercentage: values.discountPercentage,
      memo: values.showMemo ? values.memo : null,
      footer: values.showFooter ? values.footer : null,
      retentionTaxRateId: values.retentionTaxRate?.id,
      projectId: values.project?.id,
      subprojectId: values.subproject?.id,
      originalFinancialDocumentId: values.originalFinancialDocumentId,
      complianceExtraFields: values.complianceExtraFields
        ? mapComplianceExtraFieldsForUpsert([values.complianceExtraFields])
        : undefined,
      logoFile: values.logoFile,
      exchangeRate: values.exchangeRate?.toString(),
    }

    const handledUpsert = handleResult(
      await upsertSaleFinancialDocument({
        variables: {
          ...baseSubmitData,
          id: isSalesInvoicePersisted && !createCreditNote ? salesInvoiceId : undefined,
          currency: values.currency,
          operationDate: values.operationDate,
          dueDate: values.dueDate,
          legalEntityId: values.legalEntity?.id,
          contactId: values.contact?.id,
          documentType: values.documentType,
          serialSequenceId: values.salesInvoiceSequence?.id,
        },
      }),
      (errors) => {
        form.setErrors({ ...errors, salesInvoiceSequence: errors.salesInvoiceSequenceId })
        handleResultWithFlash()
      }
    )

    if (handledUpsert.success) {
      setSalesInvoiceId(handledUpsert.data.financialDocument?.id ?? -1)
      trackFormSaveAction(salesInvoiceId?.toString(), form.values, form.initialValues)

      switch (values.action) {
        case 'issuing':
          if (validateIssuingParams(values)) {
            const result = await validateSaleForIssueFinancialDocument({
              variables: {
                ...baseSubmitData,
                id: handledUpsert.data.financialDocument?.id ?? -1,
                currency: values.currency ?? '',
                operationDate: values.operationDate ?? '',
                dueDate: values.dueDate ?? '',
                legalEntityId: values.legalEntity?.id ?? -1,
                contactId: values.contact?.id ?? -1,
                documentType: values.documentType ?? '',
                serialSequenceId: values.salesInvoiceSequence?.id ?? -1,
              },
              // We cannot use handleResult here because we need to handle the errors in a different way
              // The default behaviour from handleResult is camelcase errors fields.
              // Thats ok for {"field": "serial_sequence_id"} -> it becomes "serialSequenceId"
              // But in case of verifactu errors in a line item we will receive:
              //  {"field": "compliance_extra_fields.es-verifactu-op-class.ef444ce7-b8b6-417e-8427-04288252e8e1"}
              // If we camelcase that, the lineItemId will become "ef444ce7B8b6417e842704288252e8e1" impossible to use to
              // find its corresponding lineItemId
              onCompleted: (data) => {
                const dataResult =
                  data.financeMutations.validateSaleForIssueFinanceFinancialDocument

                const errors: (StructuredError | SimpleError)[] = dataResult.errors

                if (errors.length > 0) {
                  handleErrorValidateForIssuing(errors, complianceExtraFieldsConfigs, form)
                } else {
                  handleSuccessValidateForIssuing(dataResult.financialDocument?.id ?? -1)
                }
              },
            })
          } else {
            app.setFlash({
              type: 'wrong',
              message: i18n.t('finance.invoices.detail.issue_validation_error'),
            })
          }
          break
        case 'saving':
          app.setFlash({ type: 'correct', message: i18n.t('finance.invoices.detail.success_save') })
          onClose()
          break
      }
    }

    return handledUpsert
  }

  const formIsValidToIssueInvoice = () => {
    return isValidForIssuing(form.values, validateLineItems, validateComplianceExtraFields)
  }

  const activatedTaxBridgeActivations = useUnsafeMemo(
    () => taxBridgeActivations.filter((taxBridgeActivation) => !!taxBridgeActivation.activatedAt),
    [taxBridgeActivations]
  )

  const activatedTaxBridgeLegalEntityIds = useUnsafeMemo(
    () =>
      activatedTaxBridgeActivations.map((taxBridgeActivation) => taxBridgeActivation.legalEntityId),
    [activatedTaxBridgeActivations]
  )

  const excludedSpanishLegalEntityIds = useUnsafeMemo(
    () =>
      taxAgencyIntegrationsEnabled
        ? spanishLegalEntities
            .map((le) => le.id)
            .filter((id) => !activatedTaxBridgeLegalEntityIds.includes(id))
        : [],
    [spanishLegalEntities, activatedTaxBridgeLegalEntityIds, taxAgencyIntegrationsEnabled]
  )

  defaultLegalEntity = getLegalEntityToInitialize(
    activatedTaxBridgeActivations,
    defaultLegalEntity,
    excludedSpanishLegalEntityIds,
    spanishLegalEntities,
    nonSpanishLegalEntity,
    originPageInvoices
  )

  const form = useForm<SaleInvoiceFormFields>({
    enableReinitialize: true,
    defaultValues: createDefaultFormValues(
      saleInvoice,
      createCreditNote,
      defaultCurrency,
      defaultLegalEntity,
      contact,
      financialProject,
      complianceExtraFieldsConfigs,
      excludedSpanishLegalEntityIds,
      defaultSalesDocumentTemplates,
      defaultComplianceExtraFieldsSettings
    ),
    validate: validateSaleInvoiceForm,
    validateOnChange: false,
    onSubmit: async (dataParam) => {
      return await onSubmit(dataParam)
    },
  })

  useUnsafeEffect(() => {
    const logoFileToFetch = form.values.salesDocumentTemplate
      ? form.values.salesDocumentTemplate?.logoFile
      : saleInvoice?.logoFile

    if (logoFileToFetch) {
      fetchLogoFile(logoFileToFetch).then((file) => {
        form.setFieldValue('logoFile', file)
      })
    } else {
      form.setFieldValue('logoFile', undefined)
    }
  }, [saleInvoice?.logoFile, form.values.salesDocumentTemplate?.logoFile])

  const generatePreview = useUnsafeCallback(
    async (values: SaleInvoiceFormFields) => {
      const result = await previewFinancialDocument({
        variables: {
          currency: values.currency,
          serialSequenceId: values.salesInvoiceSequence?.id,
          operationDate: values.operationDate,
          issueDate: values.issueDate,
          dueDate: values.dueDate,
          legalEntityId: values.legalEntity?.id,
          contactId: values.contact?.id,
          documentType: values.documentType,
          lineItems: mapLineItemsForPreview(values.lineItems),
          discountPercentage: values.discountPercentage,
          memo: values.showMemo ? values.memo : null,
          footer: values.showFooter ? values.footer : null,
          retentionTaxRateId: values.retentionTaxRate?.id,
          projectId: values.project?.id,
          subprojectId: values.subproject?.id,
          logoFile: values.logoFile,
        },
      })

      const handled = handleResult(result, () => {})
      if (handled.success) {
        setPreviewHtml(handled.data.financialDocumentPreview?.htmlContent)
      }
    },
    [previewFinancialDocument]
  )

  const debouncedGeneratePreviewRef = useUnsafeMemo(
    () => debounce((values: SaleInvoiceFormFields) => generatePreview(values), 300),
    [generatePreview]
  )

  useUnsafeEffect(() => {
    debouncedGeneratePreviewRef(form.values)
    return () => debouncedGeneratePreviewRef.cancel()
  }, [form.values, debouncedGeneratePreviewRef])

  const title = createCreditNote
    ? i18n.t('finance.sale_invoices.form.actions.credit_note.create')
    : isSalesInvoicePersisted && saleInvoice?.documentType === 'credit_note'
    ? i18n.t('finance.sale_invoices.form.actions.credit_note.edit')
    : isSalesInvoicePersisted
    ? i18n.t('finance.sale_invoices.form.edit_invoice')
    : i18n.t('finance.sale_invoices.datatable.create_invoice')

  const previousInvoiceNumber = createCreditNote
    ? saleInvoice?.documentNumber
    : saleInvoice?.parentFinancialDocument?.documentNumber || null

  if (error)
    return (
      <Sidepanel title={title} width="extended" open onClose={onClose}>
        <NotFound />
      </Sidepanel>
    )

  const canCreateCreditNote =
    !!saleInvoice?.validatedAt && !createCreditNote && !saleInvoice.childFinancialDocument

  const options = isSalesInvoicePersisted
    ? [
        ...(saleInvoice?.documentType === 'invoice'
          ? [
              {
                title: i18n.t('finance.sale_invoices.datatable.actions.duplicate'),
                onClick: () => {
                  track(trackingEvents.accounting.saleInvoices.clickDuplicateInvoice)
                  handleDuplicateSaleInvoice({
                    invoiceId: salesInvoiceId,
                    currentEmployeeId: currentEmployee?.id || -1,
                  }).then((id) => {
                    if (id) {
                      onClose()
                    }
                  })
                },
              },
            ]
          : []),
        ...(!saleInvoice?.validatedAt || !saleInvoice?.registerSaleAt
          ? [
              {
                title: i18n.t('finance.sale_invoices.datatable.actions.delete_draft_invoice'),
                onClick: () => {
                  showArchiveSaleInvoiceDialog({
                    invoiceId: salesInvoiceId,
                    callback: onClose,
                  })
                  track(trackingEvents.accounting.saleInvoices.clickDeleteDraftInvoice)
                },
                danger: true,
              },
            ]
          : canCreateCreditNote
          ? [
              {
                title: i18n.t('finance.sale_invoices.form.actions.credit_note.create'),
                onClick: () => setCreateCreditNote(true),
              },
            ]
          : []),
      ]
    : undefined

  const handleIssueInvoice = () => {
    form
      .setValues({
        ...form.values,
        action: 'issuing',
      })
      .then(() => {
        form.handleSubmit()
      })
  }

  const onClickSaveButton = () => {
    track(trackingEvents.accounting.saleInvoices.clickSaveInvoice, {
      origin: eventOrigin,
    })
    onClickSaveDraft()
  }

  const onClickSaveDraft = () => {
    form
      .setValues({
        ...form.values,
        action: 'saving',
      })
      .then(() => {
        form.handleSubmit()
      })
  }

  const footerActions = () => {
    if (isSalesInvoicePersisted && !createCreditNote && !!saleInvoice?.validatedAt) {
      return null
    }

    return (
      <Box flexDirection="rowReverse" gap="s12">
        <Button
          onClick={handleIssueInvoice}
          type="primary"
          loading={form.isSubmitting}
          disabled={
            !formIsValidToIssueInvoice() ||
            ((!!saleInvoice?.validatedAt || !!saleInvoice?.registerSaleAt) && !createCreditNote)
          }
        >
          {!!createCreditNote || form.values.documentType == 'credit_note'
            ? i18n.t('finance.sale_invoices.form.actions.credit_note.issue')
            : i18n.t('finance.sale_invoices.form.actions.issue_invoice')}
        </Button>
        <Button
          onClick={onClickSaveButton}
          type="secondary"
          loading={form.isSubmitting}
          disabled={!form.dirty || (!!saleInvoice?.registerSaleAt && !createCreditNote)}
        >
          {i18n.t('finance.sale_invoices.form.actions.save_draft')}
        </Button>
      </Box>
    )
  }

  return (
    <Sidepanel
      title={
        <Box flexDirection="row" alignItems="center" justifyContent="center" width="full" gap="s8">
          <Text size="300" color="textPrimary" weight="semibold">
            {title}
          </Text>
          {saleInvoice && !createCreditNote && (
            <SaleInvoiceStatus
              validated={!!saleInvoice.validatedAt}
              registered={!!saleInvoice.registerSaleAt}
            />
          )}
        </Box>
      }
      width="extended"
      open
      onClose={onClose}
      closeLabel={i18n.t('common.close')}
      boxPadding={false}
      footer={footerActions()}
      options={options?.length ? options : undefined}
    >
      {loading && <Loading />}
      {!loading && (
        <SaleInvoiceForm
          form={form}
          previousInvoiceNumber={previousInvoiceNumber}
          previewHtml={previewHtml}
          readOnly={!!saleInvoice?.validatedAt && !createCreditNote}
          defaultTaxRate={defaultTaxRate}
          disableContact={contactsCounter == 0 || !!contact}
          disableRetentionTaxRate={personalIncomeTaxRatesCounter == 0}
          disableProject={projectsCounter == 0 || !!financialProject}
          projectsModuleEnabled={projectsModuleEnabled}
          complianceExtraFieldsConfigs={complianceExtraFieldsConfigs}
          minDateProp={
            createCreditNote
              ? saleInvoice?.documentDate
              : saleInvoice?.parentFinancialDocument?.documentDate
          }
          excludeLegalEntityIds={excludedSpanishLegalEntityIds}
          salesDocumentTemplatesEnabled={canManageSalesDocumentTemplates}
          defaultComplianceExtraFieldsSettings={defaultComplianceExtraFieldsSettings}
        />
      )}
    </Sidepanel>
  )
}

export default SaleInvoicesUpsertSidePanel
